<template>
  <section class="tw-page roster-page">
    <div class="tw-container">
      <PageHeader
        title="My Team"
        subtitle="Review and manage the wrkrs linked to your account."
      >
        <template #actions>
          <b-button
            v-if="canInviteToTeam"
            type="is-primary"
            size="is-medium"
            class="invite-button"
            :disabled="!user || !user._id"
            @click="showInviteModal = true"
          >
            Invite to My Team
          </b-button>
          <b-button
            v-if="canRequestNewUser"
            size="is-medium"
            class="invite-button"
            @click="showRequestUserModal = true"
          >
            Request a new user
          </b-button>
        </template>
      </PageHeader>

      <div v-if="isLoading" class="tw-state">
        <b-loading :is-full-page="false" :active="true" />
        <p>Loading your profile...</p>
      </div>

      <template v-else>
        <div v-if="rosterLoading" class="tw-state">
          <b-loading :is-full-page="false" :active="true" />
          <p>Loading team...</p>
        </div>

        <div v-else-if="rosterError" class="tw-state tw-state--error">
          <p>{{ rosterError }}</p>
        </div>

        <section v-else class="team-org-card tw-card">
          <div class="team-org-card__header">
            <div>
              <p class="section-eyebrow">Reporting view</p>
              <h2>{{ user && user.isAdmin ? 'Company Roster' : 'Your Team Map' }}</h2>
            </div>
            <p class="team-org-card__hint">
              Select a member to view their profile.
            </p>
          </div>

          <div v-if="companyMembersLoading || rosterLoading" class="tw-state">
            <b-loading :is-full-page="false" :active="true" />
            <p>Loading members...</p>
          </div>

          <template v-else>
            <div v-if="teamTreeRoot" class="team-org-tree">
              <OrgTreeNode
                :node="teamTreeRoot"
                :show-persona-details="true"
                @select="openProfile"
              />
            </div>

            <p v-if="!hasRoster && !user.isAdmin" class="team-org-card__empty">
              You currently have no team members. Ask your tenant owner or a company admin to add people to your team.
            </p>

            <section v-if="orphanedMembers.length" class="roster-orphans">
              <div class="roster-orphans__header">
                <h3 class="roster-orphans__title">Not yet in any team</h3>
                <p class="roster-orphans__hint">These company members aren't part of anyone's reporting structure.</p>
              </div>
              <div class="roster-orphans__grid">
                <div
                  v-for="member in orphanedMembers"
                  :key="member._id"
                  class="roster-orphans__card"
                  role="button"
                  tabindex="0"
                  @click="openProfile(member)"
                  @keydown.enter.prevent="openProfile(member)"
                >
                  <span class="roster-orphans__name">{{ member.firstName }} {{ member.lastName }}</span>
                  <span class="roster-orphans__email">{{ member.email }}</span>
                </div>
              </div>
            </section>
          </template>
        </section>
      </template>
    </div>
    <RosterInviteModal
      v-model="showInviteModal"
      :invite-link="inviteLink"
      :invite-link-loading="inviteLinkLoading"
      :invite-link-error="inviteLinkError"
      :invite-link-expires-at="inviteLinkExpiresAt"
    />
    <RequestNewUserModal
      v-model="showRequestUserModal"
      :config="config"
      @requested="onNewUserRequested"
    />
  </section>
</template>

<script>
import { useUserStore } from '@/stores/userStore'
import { mapState } from 'pinia'
import PageHeader from '@/components/layout/PageHeader.vue'
import RosterInviteModal from '@/components/roster/RosterInviteModal.vue'
import RequestNewUserModal from '@/components/roster/RequestNewUserModal.vue'
import OrgTreeNode from '@/components/auth/OrgTreeNode.vue'

export default {
  name: 'RosterGrid',
  components: {
    PageHeader,
    RosterInviteModal,
    RequestNewUserModal,
    OrgTreeNode,
  },
  data() {
    return {
      rosterMembers: [],
      rosterLoading: false,
      rosterError: null,
      lastRosterKey: null,
      companyMembers: [],
      companyMembersLoading: false,
      showInviteModal: false,
      showRequestUserModal: false,
      inviteToken: '',
      inviteLinkExpiresAt: '',
      inviteLinkLoading: false,
      inviteLinkError: '',
      config: {
        headers: { Authorization: this.$cookies.get('teamwrkr-token') },
      },
    }
  },
  computed: {
    ...mapState(useUserStore, ['user', 'loading']),
    isLoading() {
      return this.loading || this.user === null
    },
    // spec-admin-gated-company-access: managers (derived isManager) and company admins can ask the
    // owner / an admin to add a brand-new person to the company and their team. The server re-checks.
    // Access & Onboarding Model v1: only the tenant owner / a company admin (or a super user) adds
    // people to a team — invites and team invite links are refused (403 admin_only) for anyone else.
    canInviteToTeam() {
      return this.user?.isAdmin === true || this.user?.isSuperUser === true
    },
    canRequestNewUser() {
      return Boolean(this.user?.company) && (this.user?.isManager === true || this.user?.isAdmin === true)
    },
    hasRoster() {
      return Array.isArray(this.user?.roster) && this.user.roster.length > 0
    },
    // spec-reporting-hierarchy: the link carries a signed invite token from the backend (bound to
    // this manager and company, 72h; it dies early if the manager is deactivated or leaves). Without a valid token the link still works, but joining
    // becomes a request the manager has to approve.
    inviteLink() {
      if (!this.inviteToken) return ''
      const baseUrl = process.env.VUE_APP_BASE_URL || window.location.origin
      const managerId = this.user?._id || ''
      return `${baseUrl}/welcome?team=${encodeURIComponent(managerId)}&invite=${encodeURIComponent(this.inviteToken)}`
    },
    peopleById() {
      const pool = this.user?.isAdmin
        ? [this.user, ...this.companyMembers]
        : [this.user, ...this.rosterMembers]
      return pool.filter((person) => person?._id).reduce((people, person) => {
        people[String(person._id)] = person
        return people
      }, {})
    },
    teamTreeRoot() {
      if (!this.user?._id) return null
      return this.buildTeamTree(this.user, null, new Set())
    },
    orphanedMembers() {
      if (!this.user?.isAdmin || !this.teamTreeRoot) return []
      const inTree = new Set()
      this.collectTreeIds(this.teamTreeRoot, inTree)
      return this.companyMembers.filter(
        (m) => m?._id && !inTree.has(String(m._id)) && String(m._id) !== String(this.user._id)
      )
    },
  },
  watch: {
    showInviteModal(isOpen) {
      if (isOpen) this.fetchInviteLink()
    },
    user: {
      handler(newUser) {
        if (newUser?.isAdmin) {
          this.fetchCompanyMembers()
          return
        }

        if (!Array.isArray(newUser?.roster) || !newUser.roster.length) {
          this.rosterMembers = []
          this.lastRosterKey = null
          return
        }

        const rosterKey = newUser.roster.join(',')
        if (this.lastRosterKey === rosterKey && this.rosterMembers.length) {
          return
        }

        this.lastRosterKey = rosterKey
        this.fetchRoster()
      },
      immediate: true,
      deep: false,
    },
  },
  beforeCreate() {
    if (!this.$cookies.isKey('teamwrkr-token')) {
      this.$router.push('/verify')
    }
  },
  created() {
    const userStore = useUserStore()
    const token = this.$cookies.get('teamwrkr-token')
    userStore.fetchUser(token)
  },
  methods: {
    collectTreeIds(node, set) {
      if (!node?.key) return
      set.add(String(node.key))
      ;(node.children || []).forEach((child) => this.collectTreeIds(child, set))
    },
    buildTeamTree(person, managerKey, visited) {
      if (!person?._id || visited.has(person._id)) return null

      const nextVisited = new Set(visited)
      nextVisited.add(person._id)

      return {
        key: person._id,
        managerKey,
        persona: person,
        children: this.directReportIdsFor(person)
          .map((memberId) => this.buildTeamTree(this.peopleById[memberId], person._id, nextVisited))
          .filter(Boolean),
      }
    },
    directReportIdsFor(person) {
      const roster = Array.isArray(person?.roster) ? person.roster : []
      return roster
        .map((entry) => (entry && typeof entry === 'object' ? entry._id : entry))
        .filter((id) => id && this.peopleById[id])
    },
    openProfile(person) {
      if (!person?._id) return
      this.$router.push(`/member/${person._id}`)
    },
    // A manager's request was sent: refresh the user (and so the team map) from the server.
    onNewUserRequested() {
      const token = this.$cookies.get('teamwrkr-token')
      if (token) useUserStore().fetchUser(token, { force: true })
      if (this.user?.isAdmin) this.fetchCompanyMembers()
    },
    async fetchInviteLink() {
      this.inviteLinkLoading = true
      this.inviteLinkError = ''
      try {
        const response = await this.axios.post(
          `${process.env.VUE_APP_API_URL}/users/roster/invite-link`,
          {},
          this.config,
        )
        this.inviteToken = response.data?.token || ''
        this.inviteLinkExpiresAt = response.data?.expiresAt || ''
      } catch (error) {
        console.error('Error creating team invite link:', error)
        this.inviteToken = ''
        this.inviteLinkExpiresAt = ''
        this.inviteLinkError = error?.response?.data?.message || 'Unable to create an invite link right now.'
      } finally {
        this.inviteLinkLoading = false
      }
    },
    async fetchCompanyMembers() {
      this.companyMembersLoading = true
      try {
        const response = await this.axios.get(
          `${process.env.VUE_APP_API_URL}/users/company-members`,
          this.config,
        )
        this.companyMembers = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        console.error('Error fetching company members:', error)
      } finally {
        this.companyMembersLoading = false
      }
    },
    async fetchRoster() {
      if (!this.hasRoster) return

      this.rosterLoading = true
      this.rosterError = null

      try {
        const response = await this.axios.get(
          `${process.env.VUE_APP_API_URL}/users/roster`,
          this.config,
        )
        this.rosterMembers = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        console.error('Error fetching team:', error)
        this.rosterError = 'Unable to load your team right now.'
      } finally {
        this.rosterLoading = false
      }
    },
  },
}
</script>

<style lang="scss">
.roster-page {
  .invite-button {
    white-space: nowrap;
  }
}

.team-org-card {
  margin-top: $space-6;
  padding: $space-5;
}

.team-org-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: $space-4;
  margin-bottom: $space-5;

  h2 {
    margin: 0;
    color: $color-text;
    font-size: $font-size-xl;
    font-weight: 800;
  }
}

.team-org-card__hint {
  max-width: 18rem;
  margin: 0;
  color: $color-text-muted;
  line-height: 1.45;
}

.team-org-tree {
  max-width: 920px;
  margin: 0 auto;
  padding-bottom: $space-2;
}

.team-org-card__empty {
  margin: $space-4 0 0;
  padding: $space-3;
  border: 1px dashed $color-border;
  border-radius: $radius-md;
  background: $color-surface-soft;
  color: $color-text-muted;
  text-align: center;
}

.roster-orphans {
  margin-top: $space-6;
  padding-top: $space-5;
  border-top: 1px solid $color-border-muted;
}

.roster-orphans__header {
  margin-bottom: $space-4;

  h3 {
    margin: 0 0 $space-1;
    font-size: $font-size-lg;
    font-weight: 700;
    color: $color-text;
  }
}

.roster-orphans__hint {
  margin: 0;
  font-size: $font-size-sm;
  color: $color-text-muted;
}

.roster-orphans__grid {
  display: flex;
  flex-wrap: wrap;
  gap: $space-3;
}

.roster-orphans__card {
  display: flex;
  flex-direction: column;
  gap: $space-1;
  padding: $space-3 $space-4;
  background: $color-surface-soft;
  border: 1px solid $color-border;
  border-radius: $radius-md;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
  min-width: 180px;

  &:hover,
  &:focus {
    border-color: $color-primary;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    outline: none;
  }
}

.roster-orphans__name {
  font-weight: 600;
  color: $color-text;
  font-size: $font-size-md;
}

.roster-orphans__email {
  font-size: $font-size-sm;
  color: $color-text-muted;
}

@media (max-width: 600px) {
  .tw-page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .team-org-card {
    padding: $space-3;
  }

  .team-org-card__header {
    flex-direction: column;
  }
}
</style>
